/**
 * nodeDelete.ts - Centralized delete logic for automation flow nodes
 *
 * Provides pure functions for:
 * - Protecting trigger nodes and default segments from direct deletion
 * - Cascading switch deletion to segment nodes, hidden switch node, and phantom target
 * - Reconnecting edges when a mid-chain node is deleted (A→B→C becomes A→C)
 * - Re-laying out the graph after structural changes
 *
 * Related:
 *   - utils/nodeReordering.ts - Reuses getChildNodeIds, getDescendantNodeIds
 *   - utils/layoutEngine.ts - Re-applies layout after deletion
 *   - hooks/useCanvasKeyboardShortcuts.ts - Keyboard delete handler
 *   - hooks/useContextMenuActions.ts - Context menu delete handler
 */

import type * as Api from '@ViewModels';
import type { Edge, Node } from '@xyflow/react';
import type { BaseNodeData, SegmentNodeData } from '../types';
import type { AutomationStepNodeData } from '../types/stepNodes';
import { NODE_TYPES } from '../types';
import { getChildNodeIds, getDescendantNodeIds } from './nodeReordering';
import { applyLayout } from './layoutEngine';

/**
 * Metadata about a deleted step, extracted for API persistence.
 */
export interface DeletedStepInfo {
	/** The React Flow node ID that was deleted */
	nodeId?: string;
	/** The server-side step ID */
	stepId: string;
	/** The automation step type enum for the API call */
	stepType: Api.AutomationStepType;
	/**
	 * The template ID that owns this step. For root-level steps this is the main
	 * template ID; for branch steps inside a switch case this is the child
	 * template ID stored on the case.
	 */
	ownerTemplateId?: string;
}

/**
 * Result of a deleteNodes operation.
 */
export interface DeleteNodesResult {
	/** Updated node list with deleted nodes removed */
	nodes: Node<BaseNodeData>[];
	/** Updated edge list with orphaned edges removed and reconnections applied */
	edges: Edge[];
	/** Number of nodes actually deleted */
	deletedCount: number;
	/** IDs of nodes that were requested for deletion but protected */
	protectedNodeIds: string[];
	/** Metadata for each deleted step node (used to persist deletions to the API) */
	deletedSteps: DeletedStepInfo[];
}

/**
 * Check whether a node is protected from direct deletion.
 *
 * Protected nodes:
 * - Trigger nodes (the automation entry point)
 * - Default segments (the fallback branch of a switch)
 *
 * Protected nodes CAN still be removed during cascade deletion (e.g., when
 * the entire switch structure is removed). The `isCascade` flag controls this.
 */
export function isProtectedNode(node: Node<BaseNodeData>, isCascade = false): boolean {
	// Trigger nodes are always protected, even during cascade.
	if (node.type === NODE_TYPES.TRIGGER) {
		return true;
	}

	// Default segments are protected from direct deletion but removed during cascade.
	if (node.type === NODE_TYPES.SEGMENT && !isCascade) {
		const segmentData = node.data as SegmentNodeData;
		if (segmentData.isDefault) {
			return true;
		}
	}

	return false;
}

/**
 * Collect all nodes that must be cascade-deleted when a switch's visual parent
 * (the step node that feeds into segments) is deleted.
 *
 * A "visual parent" is the step whose outgoing edges target segment nodes.
 * When this node is deleted, the entire switch structure becomes orphaned:
 * - All segment nodes for this switch
 * - The hidden switch node
 * - The phantom target node
 * - All descendants under each segment
 *
 * This function looks at the requested deletion set and finds any node whose
 * outgoing edges target segment nodes. For each such node, it collects the
 * full switch subtree.
 */
export function collectSwitchCascadeIds(
	requestedIds: Set<string>,
	nodes: Node<BaseNodeData>[],
	edges: Edge[]
): Set<string> {
	const cascadeIds = new Set<string>();
	const nodeMap = new Map<string, Node<BaseNodeData>>();
	for (const node of nodes) {
		nodeMap.set(node.id, node);
	}

	for (const nodeId of requestedIds) {
		const node = nodeMap.get(nodeId);
		if (!node) {
			continue;
		}

		// Check if this node's children include segment nodes.
		const childIds = getChildNodeIds(nodeId, edges);
		const childSegments = childIds
			.map(id => nodeMap.get(id))
			.filter((n): n is Node<BaseNodeData> => n !== undefined && n.type === NODE_TYPES.SEGMENT);

		if (childSegments.length === 0) {
			continue;
		}

		// This node is a visual parent of a switch. Collect the entire switch subtree.
		for (const segment of childSegments) {
			const segmentData = segment.data as SegmentNodeData;
			const switchNodeId = segmentData.parentSwitchNodeId;

			// Find ALL segments belonging to this switch (not just ones connected to this node).
			const allSwitchSegments = nodes.filter(n => {
				if (n.type !== NODE_TYPES.SEGMENT) {
					return false;
				}
				return (n.data as SegmentNodeData).parentSwitchNodeId === switchNodeId;
			});

			for (const seg of allSwitchSegments) {
				cascadeIds.add(seg.id);
				// Collect all descendants under each segment.
				const descendants = getDescendantNodeIds(seg.id, edges);
				for (const descendantId of descendants) {
					cascadeIds.add(descendantId);
				}
			}

			// Add the hidden switch node itself.
			if (nodeMap.has(switchNodeId)) {
				cascadeIds.add(switchNodeId);
			}

			// Add the phantom target node.
			const phantomTargetId = `phantom-target-${switchNodeId}`;
			if (nodeMap.has(phantomTargetId)) {
				cascadeIds.add(phantomTargetId);
			}
		}
	}

	return cascadeIds;
}

/**
 * Collect all nodes that must be cascade-deleted when a segment node is deleted.
 *
 * When deleting a segment (non-default case), we want to remove:
 * - The segment node itself
 * - All descendant nodes under that segment (steps inside the branch)
 *
 * Additionally, if deleting the segment would leave a switch with only the default
 * case remaining, we remove the entire switch structure:
 * - Default segment + all of its descendants
 * - The hidden switch node
 * - The phantom target node
 */
export function collectSegmentCascadeIds(
	requestedIds: Set<string>,
	nodes: Node<BaseNodeData>[],
	edges: Edge[]
): Set<string> {
	const cascadeIds = new Set<string>();
	const nodeMap = new Map<string, Node<BaseNodeData>>();
	for (const node of nodes) {
		nodeMap.set(node.id, node);
	}

	// Group requested segment deletions by parent switch.
	const segmentsToDeleteBySwitch = new Map<string, string[]>();

	for (const nodeId of requestedIds) {
		const node = nodeMap.get(nodeId);
		if (!node || node.type !== NODE_TYPES.SEGMENT) {
			continue;
		}

		const segmentData = node.data as SegmentNodeData;
		// Default segments are protected from direct deletion; ignore if present.
		if (segmentData.isDefault) {
			continue;
		}

		const list = segmentsToDeleteBySwitch.get(segmentData.parentSwitchNodeId) ?? [];
		list.push(nodeId);
		segmentsToDeleteBySwitch.set(segmentData.parentSwitchNodeId, list);

		// Always delete the segment's descendants.
		const descendants = getDescendantNodeIds(nodeId, edges);
		for (const descendantId of descendants) {
			cascadeIds.add(descendantId);
		}
	}

	// If we're deleting all non-default segments for a switch, remove the whole switch.
	for (const [switchNodeId, segmentIdsToDelete] of segmentsToDeleteBySwitch.entries()) {
		const allSegments = nodes.filter(n => {
			if (n.type !== NODE_TYPES.SEGMENT) {
				return false;
			}
			return (n.data as SegmentNodeData).parentSwitchNodeId === switchNodeId;
		});

		const nonDefaultSegments = allSegments.filter(s => !(s.data as SegmentNodeData).isDefault);
		const deletingAllNonDefault = nonDefaultSegments.every(s => segmentIdsToDelete.includes(s.id));

		if (!deletingAllNonDefault) {
			continue;
		}

		// Add ALL segments (including default) + descendants.
		for (const seg of allSegments) {
			cascadeIds.add(seg.id);
			const descendants = getDescendantNodeIds(seg.id, edges);
			for (const descendantId of descendants) {
				cascadeIds.add(descendantId);
			}
		}

		// Add hidden switch node.
		if (nodeMap.has(switchNodeId)) {
			cascadeIds.add(switchNodeId);
		}

		// Add phantom target node.
		const phantomTargetId = `phantom-target-${switchNodeId}`;
		if (nodeMap.has(phantomTargetId)) {
			cascadeIds.add(phantomTargetId);
		}
	}

	return cascadeIds;
}

/**
 * Build reconnection edges for mid-chain deletions.
 *
 * When node B is deleted from chain A→B→C, we need to create edge A→C.
 * This only applies when B has exactly ONE incoming edge and ONE outgoing edge
 * (simple chain case). Multi-parent or multi-child nodes do not reconnect.
 *
 * The reconnection edge preserves the sourceHandle from the incoming edge
 * (A→B's sourceHandle), since that determines which output port of A is used.
 */
export function buildReconnectionEdges(deletionIds: Set<string>, edges: Edge[]): Edge[] {
	const reconnections: Edge[] = [];

	for (const deletedId of deletionIds) {
		// Find all incoming edges to this node.
		const incomingEdges = edges.filter(e => e.target === deletedId && !deletionIds.has(e.source));

		// Find all outgoing edges from this node.
		const outgoingEdges = edges.filter(e => e.source === deletedId && !deletionIds.has(e.target));

		// Only reconnect in the simple single-in, single-out case.
		if (incomingEdges.length !== 1 || outgoingEdges.length !== 1) {
			continue;
		}

		const incoming = incomingEdges[0];
		const outgoing = outgoingEdges[0];

		reconnections.push({
			id: `edge-${incoming.source}-${outgoing.target}`,
			source: incoming.source,
			target: outgoing.target,
			sourceHandle: incoming.sourceHandle,
			type: 'default',
		});
	}

	return reconnections;
}

/**
 * Delete nodes from the automation flow with protection, cascade, and reconnection.
 *
 * This is the single entry point for all node deletion operations (keyboard and
 * context menu). It:
 * 1. Filters out protected nodes (triggers, default segments) from direct deletion
 * 2. Cascades switch deletion to all related nodes (segments, hidden switch, phantom target)
 * 3. Reconnects edges when a mid-chain node is removed
 * 4. Re-layouts the graph via applyLayout
 *
 * @param requestedNodeIds - IDs of nodes the user wants to delete
 * @param nodes - Current node list
 * @param edges - Current edge list
 * @returns DeleteNodesResult with updated nodes, edges, and metadata
 */
export function deleteNodes(requestedNodeIds: string[], nodes: Node<BaseNodeData>[], edges: Edge[]): DeleteNodesResult {
	if (requestedNodeIds.length === 0) {
		return { nodes, edges, deletedCount: 0, protectedNodeIds: [], deletedSteps: [] };
	}

	const nodeMap = new Map<string, Node<BaseNodeData>>();
	for (const node of nodes) {
		nodeMap.set(node.id, node);
	}

	// Step 1: Partition requested IDs into deletable and protected.
	const directDeleteIds = new Set<string>();
	const protectedNodeIds: string[] = [];

	for (const nodeId of requestedNodeIds) {
		const node = nodeMap.get(nodeId);
		if (!node) {
			continue;
		}
		if (isProtectedNode(node, false)) {
			protectedNodeIds.push(nodeId);
		} else {
			directDeleteIds.add(nodeId);
		}
	}

	if (directDeleteIds.size === 0) {
		return { nodes, edges, deletedCount: 0, protectedNodeIds, deletedSteps: [] };
	}

	// Step 2: Collect cascade IDs (switch subtrees).
	const cascadeIds = collectSwitchCascadeIds(directDeleteIds, nodes, edges);

	// Step 2b: Collect cascade IDs for segment deletions (delete branch descendants,
	// and collapse the entire switch when only the default case would remain).
	const segmentCascadeIds = collectSegmentCascadeIds(directDeleteIds, nodes, edges);

	// Merge direct + cascades into the final deletion set.
	const allDeletionIds = new Set<string>(directDeleteIds);
	for (const id of cascadeIds) {
		allDeletionIds.add(id);
	}
	for (const id of segmentCascadeIds) {
		allDeletionIds.add(id);
	}

	// Step 3: Build reconnection edges before removing anything.
	const reconnections = buildReconnectionEdges(allDeletionIds, edges);

	// Step 4: Remove nodes and orphaned edges.
	const remainingNodes = nodes.filter(n => !allDeletionIds.has(n.id));
	const remainingEdges = edges.filter(e => !allDeletionIds.has(e.source) && !allDeletionIds.has(e.target));

	// Step 5: Add reconnection edges.
	const finalEdges = [...remainingEdges, ...reconnections];

	// Step 6: Collect deleted step metadata for API persistence.
	const deletedSteps: DeletedStepInfo[] = [];
	for (const id of allDeletionIds) {
		const node = nodeMap.get(id);
		if (!node) {
			continue;
		}
		const data = node.data as AutomationStepNodeData;
		// Only step nodes have stepId/stepType — skip triggers, segments, phantom nodes, etc.
		if (data.stepId && data.stepType) {
			const info: DeletedStepInfo = {
				nodeId: id,
				stepId: data.stepId,
				stepType: data.stepType,
			};
			// Resolve the owning template: automationTemplate.id gives us the template
			// that contains this step. For branch steps this is the child template.
			if (data.automationTemplate?.id) {
				info.ownerTemplateId = data.automationTemplate.id;
			}
			deletedSteps.push(info);
		}
	}

	// Step 7: Re-layout.
	const layoutResult = applyLayout(remainingNodes, finalEdges);

	return {
		nodes: layoutResult.nodes,
		edges: layoutResult.edges,
		deletedCount: allDeletionIds.size,
		protectedNodeIds,
		deletedSteps,
	};
}
